import PropTypes from "prop-types";
import Toggle from "@shared/v2/Toggle";
import Typography from "@shared/v2/Typography";
import TextInput from "@shared/v2/TextInput";
import { TrashCanSolidV6, GripDotsVerticalRegularV6 } from "@shared/v2/Icomoon";
import Checkbox from "@shared/v2/Checkbox";
import TextButton from "@shared/v2/TextButton";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useSortable, SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { Table, TBody, TD, TH, THead, TR } from "../../Reports/components/BusinessDashboard/Table/Table";
import CollapsibleWebsiteCard from "../shared/CollapsibleWebsiteCard";

const SortableLink = ({ index, link, onChange, onDelete }) => {
  const { setNodeRef, listeners, attributes, transform, transition } = useSortable({ id: index });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <tr ref={setNodeRef} style={style} className="tw-border tw-border-solid tw-border-gray-10 tw-h-[64px]">
      {/* eslint-disable-next-line react/jsx-props-no-spreading -- dnd-kit requires spreading its sortable props */}
      <TD className="!tw-p-0 tw-w-[10px]" {...attributes} {...listeners} style={{ cursor: "grab" }}>
        <GripDotsVerticalRegularV6 size="l" />
      </TD>
      <TD className="!tw-p-0 !tw-pr-[16px] tw-w-[100px]">
        <TextInput
          placeholder="Ex. New Listings"
          value={link?.text || ""}
          onChange={(e) => onChange(index, "text", e.target.value)}
        />
      </TD>
      <TD className="!tw-p-0 !tw-pr-[4px]">
        <TextInput
          placeholder="Enter URL"
          value={link?.href || ""}
          onChange={(e) => onChange(index, "href", e.target.value)}
        />
      </TD>
      <TD className="!tw-p-0 !tw-pr-[4px] tw-w-[10px]">
        <button
          type="button"
          className="tw-flex tw-items-center tw-justify-center tw-h-[36px] tw-w-[36px] tw-border tw-border-solid tw-border-gray-10 tw-bg-gray-5 tw-cursor-pointer hover:tw-bg-gray-10"
          onClick={() => onDelete(index)}
        >
          <TrashCanSolidV6 size="l" />
        </button>
      </TD>
      <TD className="!tw-p-0 tw-text-center tw-w-[100px]">
        <Checkbox
          checked={link?.openInNewTab}
          className="tw-flex tw-flex-col"
          defaultChecked={link?.openInNewTab}
          onChange={(e) => onChange(index, "openInNewTab", e.target.checked)}
        />
      </TD>
    </tr>
  );
};

SortableLink.propTypes = {
  index: PropTypes.number.isRequired,
  link: PropTypes.shape({
    text: PropTypes.string,
    url: PropTypes.string,
    href: PropTypes.string,
    openInNewTab: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const LinksPanel = ({ data = undefined, setLinksData }) => {
  const newLink = () => ({ id: crypto.randomUUID(), text: "", url: "", openInNewTab: false });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleChange = (index, field, value) => {
    setLinksData((prev) => ({
      ...prev,
      links: prev?.links?.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const handleAddItem = () => {
    setLinksData((prev) => ({
      ...prev,
      links: [...prev.links, newLink()],
    }));
  };

  const handleDelete = (index) => {
    setLinksData((prev) => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }));
  };

  return (
    <CollapsibleWebsiteCard
      title="Links"
      disabled={!data?.isActive}
      action={
        <Toggle
          checked={data?.isActive}
          onChange={(e) => {
            setLinksData({
              ...data,
              isActive: e.target.checked,
            });
          }}
        />
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-[16px]">
        <div className="tw-flex tw-flex-col tw-gap-[16px] tw-p-[16px] tw-border tw-border-solid tw-border-gray-10 tw-rounded-xl">
          <Typography variant="subtitle-m-bold">General</Typography>
          <TextInput
            label="Title"
            placeholder="Links For This Area"
            value={data?.title}
            onChange={(e) =>
              setLinksData({
                ...data,
                title: e.target.value,
              })
            }
          />
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[16px] tw-p-[16px] tw-border tw-border-solid tw-border-gray-10 tw-rounded-xl">
          <Typography variant="subtitle-m-bold">Links</Typography>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => {
              if (over && active.id !== over.id) {
                setLinksData((prev) => ({
                  ...prev,
                  links: arrayMove(prev.links, active.id, over.id),
                }));
              }
            }}
          >
            <Table className="tw-border-separate">
              <THead className="tw-sticky tw-top-0 tw-bg-white">
                <TR className="tw-font-semibold">
                  <TH className="!tw-p-0" />
                  <TH className="!tw-p-0">Link Text</TH>
                  <TH className="!tw-p-0">Full Url</TH>
                  <TH className="!tw-p-0" />
                  <TH className="!tw-p-0">Open In New Tab</TH>
                </TR>
              </THead>
              <SortableContext items={data?.links?.map((_, i) => i)} strategy={verticalListSortingStrategy}>
                <TBody>
                  {data?.links?.map((link, index) => (
                    <SortableLink
                      key={link.id}
                      index={index}
                      link={link}
                      onChange={handleChange}
                      onDelete={handleDelete}
                    />
                  ))}
                </TBody>
              </SortableContext>
            </Table>
          </DndContext>

          <TextButton className="tw-w-[80px] !tw-p-0" onClick={() => handleAddItem()}>
            + Add Link
          </TextButton>
        </div>
      </div>
    </CollapsibleWebsiteCard>
  );
};

LinksPanel.propTypes = {
  data: PropTypes.shape({
    isActive: PropTypes.bool,
    title: PropTypes.string,
    links: PropTypes.arrayOf(PropTypes.shape()),
  }),
  setLinksData: PropTypes.func.isRequired,
};

export default LinksPanel;
